import { formatInTimeZone } from 'date-fns-tz';
import { calendarDayOfWeekFromYmd } from './coachLessonSchedule';
import { doTimesOverlap } from './timeOverlap';

export interface CourtHireBookingScheduleLike {
  id: string;
  courtId: string;
  startAt: Date | string;
  endAt: Date | string;
  status: string;
}

export interface RecurringLessonScheduleLike {
  courtIds: string[];
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  dateRange?: { startDate: string; endDate: string };
  timeZone: string;
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function isActiveCourtHireStatus(status: string): boolean {
  return status === 'pending' || status === 'confirmed';
}

/**
 * Returns true when a court hire booking overlaps a recurring lesson pattern
 * (same court, matching weekday, overlapping times, optional date-range bounds).
 */
export function courtHireBookingConflictsWithLessonSchedule(
  booking: CourtHireBookingScheduleLike,
  schedule: RecurringLessonScheduleLike
): boolean {
  if (!isActiveCourtHireStatus(booking.status)) {
    return false;
  }
  if (!schedule.courtIds.includes(booking.courtId)) {
    return false;
  }
  if (schedule.daysOfWeek.length === 0) {
    return false;
  }

  const startAt = toDate(booking.startAt);
  const endAt = toDate(booking.endAt);
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
    return false;
  }

  const bookingDate = formatInTimeZone(startAt, schedule.timeZone, 'yyyy-MM-dd');
  if (schedule.dateRange) {
    if (bookingDate < schedule.dateRange.startDate || bookingDate > schedule.dateRange.endDate) {
      return false;
    }
  }

  const bookingDay = calendarDayOfWeekFromYmd(bookingDate);
  if (!schedule.daysOfWeek.includes(bookingDay)) {
    return false;
  }

  const bookingStart = formatInTimeZone(startAt, schedule.timeZone, 'HH:mm');
  const bookingEnd = formatInTimeZone(endAt, schedule.timeZone, 'HH:mm');

  return doTimesOverlap(schedule.startTime, schedule.endTime, bookingStart, bookingEnd);
}

export function findCourtHireBookingsConflictingWithLessonSchedule(
  bookings: CourtHireBookingScheduleLike[],
  schedule: RecurringLessonScheduleLike
): CourtHireBookingScheduleLike[] {
  return bookings.filter((booking) => courtHireBookingConflictsWithLessonSchedule(booking, schedule));
}
